import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
import { Reveal, Overline, EASE } from './motion';

const PROFILES = [
  {
    key: 's92', name: 'Salamander', sub: 'bluEvolution 92', img: '/profile-92.webp',
    specs: {
      depth: '92 mm', rooms: '6', seals: '3', uf: 'Uf 0,94 W/m²K', acoustic: 'până la 47 dB',
      burglar: 'RC 2', air: 'Clasa 4', water: 'Clasa 9A', hardware: 'MACO / ROTO', glazing: 0,
    },
  },
  {
    key: 's82', name: 'Salamander', sub: 'bluEvolution 82', img: '/profile-82.webp',
    specs: {
      depth: '82 mm', rooms: '5', seals: '3', uf: 'Uf 1,1 W/m²K', acoustic: 'până la 46 dB',
      burglar: 'RC 2', air: 'Clasa 4', water: 'Clasa 9A', hardware: 'MACO / ROTO', glazing: 1,
    },
  },
  {
    key: 's76', name: 'Salamander', sub: 'GreenEvolution 76', img: '/profile-76.webp',
    specs: {
      depth: '76 mm', rooms: '5', seals: '3', uf: 'Uf 1,2 W/m²K', acoustic: 'până la 44 dB',
      burglar: 'RC 2', air: 'Clasa 4', water: 'Clasa 9A', hardware: 'MACO / ROTO', glazing: 2,
    },
  },
  {
    key: 's72', name: 'Salamander', sub: '72', img: '/profile-72.webp',
    specs: {
      depth: '72 mm', rooms: '5', seals: '2', uf: 'Uf 1,3 W/m²K', acoustic: 'până la 43 dB',
      burglar: 'RC 2', air: 'Clasa 4', water: 'Clasa 9A', hardware: 'MACO / ROTO', glazing: 3,
    },
  },
  {
    key: 'veka76', name: 'VEKA', sub: 'SOFTLINE 76 MD', img: '/veka-76.webp',
    specs: {
      depth: '76 mm', rooms: '5', seals: '3', uf: 'Uf ≤ 1,1 W/m²K', acoustic: 'până la 47 dB',
      burglar: 'RC 2', air: 'Clasa 4', water: 'Clasa 9A', hardware: 'MACO / ROTO', glazing: 4,
    },
  },
  {
    key: 'veka82', name: 'VEKA', sub: 'SOFTLINE 82 MD', img: '/veka-82.webp',
    specs: {
      depth: '82 mm', rooms: '7', seals: '3', uf: 'Uf ≤ 1,0 W/m²K', acoustic: 'până la 47 dB',
      burglar: 'RC 2', air: 'Clasa 4', water: 'Clasa 9A', hardware: 'MACO / ROTO', glazing: 5,
    },
  },
  {
    key: 'ideal8000', name: 'Aluplast', sub: 'IDEAL 8000', img: '/aluplast-8000.webp',
    specs: {
      depth: '85 mm', rooms: '6', seals: '3', uf: 'Uf 1,0 W/m²K', acoustic: 'până la 48 dB',
      burglar: 'RC 2', air: 'Clasa 4', water: 'Clasa 9A', hardware: 'MACO / ROTO', glazing: 6,
    },
  },
  {
    key: 'idealneo', name: 'Aluplast', sub: 'IDEAL neo', img: '/aluplast-neo.webp',
    specs: {
      depth: '76 mm', rooms: '5', seals: '2', uf: 'Uf 1,2 W/m²K', acoustic: 'până la 45 dB',
      burglar: 'RC 2', air: 'Clasa 4', water: 'Clasa 9A', hardware: 'MACO / ROTO', glazing: 7,
    },
  },
  {
    key: 'ideal4000', name: 'Aluplast', sub: 'IDEAL 4000', img: '/aluplast-4000.webp',
    specs: {
      depth: '70 mm', rooms: '5', seals: '2', uf: 'Uf 1,3 W/m²K', acoustic: 'până la 45 dB',
      burglar: 'RC 2', air: 'Clasa 4', water: 'Clasa 9A', hardware: 'MACO / ROTO', glazing: 8,
    },
  },
];

const BRAND_COLORS = { VEKA: '#00447C', Aluplast: '#0069B4' };
const CLASS_A_PLUS = ['s92', 'veka82', 'ideal8000'];

const getVisible = () => (window.innerWidth >= 1024 ? 4 : window.innerWidth >= 640 ? 2 : 1);

export const PvcProfiles = () => {
  const { t } = useLanguage();
  const spec = t('pvcProfiles');
  const [visible, setVisible] = useState(getVisible);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const maxIndex = Math.max(PROFILES.length - visible, 0);

  useEffect(() => {
    const onResize = () => setVisible(getVisible());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => { setIndex((i) => Math.min(i, maxIndex)); }, [maxIndex]);

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => setIndex((i) => (i >= maxIndex ? 0 : i + 1)), 3000);
    return () => clearInterval(id);
  }, [paused, maxIndex]);

  return (
    <section
      data-testid="pvc-profiles-section"
      className="max-w-[1440px] mx-auto px-6 md:px-12 py-20 md:py-28"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setTimeout(() => setPaused(false), 4000)}
    >
      <Reveal className="mb-12 md:mb-16">
        <Overline className="mb-4">{spec.overline}</Overline>
        <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mb-4">{spec.title}</h2>
        <p className="text-smoke max-w-2xl leading-relaxed">{spec.desc}</p>
      </Reveal>

      <Reveal>
        <div data-testid="pvc-carousel" className="overflow-hidden">
          <motion.div
            className="flex"
            animate={{ x: `-${index * (100 / visible)}%` }}
            transition={{ duration: 0.6, ease: EASE }}
          >
            {PROFILES.map((p) => {
              const rows = [
                ['depth', p.specs.depth], ['rooms', p.specs.rooms], ['seals', p.specs.seals], ['uf', p.specs.uf],
                ['acoustic', p.specs.acoustic], ['burglar', p.specs.burglar], ['air', p.specs.air],
                ['water', p.specs.water], ['hardware', p.specs.hardware],
              ];
              return (
                <div key={p.key} className="shrink-0 px-2.5" style={{ width: `${100 / visible}%` }}>
                  <div
                    data-testid={`pvc-profile-${p.key}`}
                    className="group relative bg-surface border border-line/10 rounded-[2rem] p-7 h-full hover:shadow-[0_24px_70px_rgba(24,26,32,0.10)] hover:-translate-y-1 transition-all duration-500"
                  >
                    <span className="absolute top-5 right-5 text-[9px] font-bold uppercase tracking-[0.2em] text-accent border border-accent/30 rounded-full px-3 py-1">
                      {spec.badges[CLASS_A_PLUS.includes(p.key) ? 0 : 1]}
                    </span>
                    {p.img ? (
                      <div className="flex justify-center mb-5">
                        <img src={p.img} alt={`${p.name} ${p.sub}`} className="h-40 w-auto object-contain" loading="lazy" />
                      </div>
                    ) : (
                      <div className="h-40 mb-5 grid place-items-center bg-surface2/70 rounded-2xl border border-dashed border-line/15">
                        <span
                          className="font-display font-semibold tracking-[0.25em]"
                          style={{ color: BRAND_COLORS[p.name] || '#00447C', fontSize: p.name.length > 5 ? '1.35rem' : '1.875rem' }}
                        >
                          {p.name.toUpperCase()}
                        </span>
                      </div>
                    )}
                    <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">{p.name}</p>
                    <h3 className="font-display text-xl font-medium tracking-tight mb-5">{p.sub}</h3>
                    <dl className="space-y-1.5 text-sm">
                      {rows.map(([k, v]) => (
                        <div key={k} className="flex justify-between gap-3 border-b border-line/5 pb-1.5">
                          <dt className="text-smoke text-xs uppercase tracking-[0.12em]">{spec.labels[k]}</dt>
                          <dd className="font-medium text-right">{v}</dd>
                        </div>
                      ))}
                      <div className="flex justify-between gap-3">
                        <dt className="text-smoke text-xs uppercase tracking-[0.12em]">{spec.labels.glazing}</dt>
                        <dd className="font-medium text-right">{spec.glazingTexts[p.specs.glazing]}</dd>
                      </div>
                    </dl>
                  </div>
                </div>
              );
            })}
          </motion.div>
        </div>

        <div className="flex justify-center gap-2 mt-8">
          {Array.from({ length: maxIndex + 1 }).map((_, d) => (
            <button
              key={d}
              type="button"
              data-testid={`pvc-carousel-dot-${d}`}
              aria-label={`Slide ${d + 1}`}
              onClick={() => setIndex(d)}
              className={`h-1 rounded-full transition-all duration-500 ${d === index ? 'w-8 bg-accent' : 'w-4 bg-line/30 hover:bg-line/50'}`}
            />
          ))}
        </div>
      </Reveal>

      <Reveal className="mt-8">
        <p className="text-smoke text-sm border-l-2 border-accent pl-4">{spec.depthNote}</p>
      </Reveal>
    </section>
  );
};
